import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ProductService from '@/services/CatalogServices/ProductService';
import { extractError } from '@/helpers/extractApiError';

const model = {
    data: null,
    submitting: false,
    loading: false,
    errors: null,
    successOnCreate: '',

    categoryList: [],
    productListOptions: [],
    tableHeaders: [],
    unitListOptions: [],
    materialTypeListOptions: [],
    cutLengthListOptions: [],
};

export const createProduct = createAsyncThunk(
    'product/createProduct',
    async (data, thunkApi) => {
        try {
            const { product_id: productId } = data;
            let response;
            if (productId) {
                response = await ProductService.updateProduct(data, productId);
            } else {
                response = await ProductService.createProduct(data);
            }
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

export const getTreeCategoryList = createAsyncThunk(
    'product/getCategoryList',
    async (_, thunkApi) => {
        try {
            const response = await ProductService.getTreeCategoryList();
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

export const getProductListOptions = createAsyncThunk(
    'product/getProductListOptions',
    async (data, thunkApi) => {
        try {
            const response = await ProductService.getProductListOptions(data);
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

export const getTableHeaders = createAsyncThunk(
    'product/getTableHeaders',
    async (data, thunkApi) => {
        try {
            const response = await ProductService.getTableHeaders(data);
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

export const viewProduct = createAsyncThunk(
    'product/viewProduct',
    async (productId, thunkApi) => {
        try {
            const response = await ProductService.viewProduct(productId);
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

export const getUnitList = createAsyncThunk(
    'product/getUnitList',
    async (_, thunkApi) => {
        try {
            const response = await ProductService.getUnitList();
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

export const getMaterialTypeList = createAsyncThunk(
    'product/getMaterialTypeList',
    async (_, thunkApi) => {
        try {
            const response = await ProductService.getMaterialTypeList();
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

export const getCutLengthList = createAsyncThunk(
    'product/getCutLengthList',
    async (_, thunkApi) => {
        try {
            const response = await ProductService.getCutLengthList();
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

const returnSuccessMessage = (productId, actionType) => {
    if (actionType === 'generate_table_image') {
        return 'The table changes were successfully rendered for preview!';
    }
    if (productId) {
        return 'Product updated successfully!';
    }
    return 'Product created successfully!';
};

const createProductSlice = createSlice({
    name: 'createProduct',
    initialState: model,
    reducers: {
        resetProductState: (state) => {
            state.data = null;
            state.submitting = false;
            state.errors = null;
        },
        resetTableHeaders: (state) => {
            state.tableHeaders = [];
        },
        resetProductStateSuccess: (state) => {
            state.successOnCreate = '';
        },
        resetProductStateError: (state) => {
            state.errors = null;
        },
    },
    extraReducers: (builder) => {
        // create product
        builder.addCase(createProduct.pending, (state) => {
            state.submitting = true;
        }).addCase(createProduct.fulfilled, (state, action) => {
            state.submitting = false;
            state.data = action.payload;
            state.successOnCreate = returnSuccessMessage(
                action.meta.arg.product_id,
                action.meta.arg.product_action_type,
            );
        }).addCase(createProduct.rejected, (state, action) => {
            state.submitting = false;
            state.errors = action.payload;
        });

        // get category list
        builder.addCase(getTreeCategoryList.pending, (state) => {
            state.loading = true;
        }).addCase(getTreeCategoryList.fulfilled, (state, action) => {
            state.loading = false;
            state.categoryList = action.payload;
        }).addCase(getTreeCategoryList.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // get product list options
        builder.addCase(getProductListOptions.pending, (state) => {
            state.loading = true;
        }).addCase(getProductListOptions.fulfilled, (state, action) => {
            state.loading = false;
            state.productListOptions = action.payload;
        }).addCase(getProductListOptions.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // get table headers
        builder.addCase(getTableHeaders.pending, (state) => {
            state.loading = true;
        }).addCase(getTableHeaders.fulfilled, (state, action) => {
            state.loading = false;
            state.tableHeaders = action.payload;
        }).addCase(getTableHeaders.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // view product for edit
        builder.addCase(viewProduct.pending, (state) => {
            state.loading = true;
        }).addCase(viewProduct.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload;
        }).addCase(viewProduct.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // get unit list
        builder.addCase(getUnitList.pending, (state) => {
            state.loading = true;
        }).addCase(getUnitList.fulfilled, (state, action) => {
            state.loading = false;
            state.unitListOptions = action.payload;
        }).addCase(getUnitList.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // get material type list
        builder.addCase(getMaterialTypeList.pending, (state) => {
            state.loading = true;
        }).addCase(getMaterialTypeList.fulfilled, (state, action) => {
            state.loading = false;
            state.materialTypeListOptions = action.payload;
        }).addCase(getMaterialTypeList.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // get cut length list
        builder.addCase(getCutLengthList.pending, (state) => {
            state.loading = true;
        }).addCase(getCutLengthList.fulfilled, (state, action) => {
            state.loading = false;
            state.cutLengthListOptions = action.payload;
        }).addCase(getCutLengthList.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });
    },
});

export const {
    resetProductState,
    resetTableHeaders,
    resetProductStateSuccess,
    resetProductStateError,
} = createProductSlice.actions;
export default createProductSlice.reducer;
